import { useContext, useEffect, useId, useState } from 'react'
import { X } from 'lucide-react'
import { BuilderContext } from './BuilderContextInstance'
import { DRY_RUN_EVENT, holdDryRun } from './rj-overrides/submitData'

/** Une valeur longue (preuve anti-robot, pièce jointe en base64) n'apprend rien en entier. */
const MAX_STRING = 80

const readable = (value) => JSON.stringify(
  value,
  (key, v) => (typeof v === 'string' && v.length > MAX_STRING ? `${v.slice(0, 24)}… (${v.length} caractères)` : v),
  2,
)

/**
 * L'envoi à blanc du builder : ALLUME l'interception pendant l'aperçu, et
 * MONTRE ce qu'un formulaire aurait envoyé.
 *
 * Monté par les éditeurs seulement — `BuilderShell` hors `readOnly`, et
 * `BuilderObjectEditor` (un contenu édité dans son modèle) —, jamais par une
 * page publique : c'est ce montage qui borne l'envoi à blanc à l'édition. Actif
 * en aperçu (bouton œil), éteint en édition — où un formulaire ne se remplit
 * pas — et au démontage. Un fournisseur en lecture seule ne l'allume jamais,
 * même si le panneau y était monté par erreur.
 *
 * Sans lui, l'aperçu afficherait le message de confirmation du formulaire comme
 * si l'envoi avait eu lieu : il dit qu'il n'a pas eu lieu, et ce qui serait
 * parti, vers quelle adresse.
 */
export const BuilderDryRunPanel = () => {
  const builder = useContext(BuilderContext)
  const previewing = Boolean(builder) && !builder.isEditMode && !builder.readOnly
  const [capture, setCapture] = useState(null)
  const titleId = useId()

  useEffect(() => {
    if (!previewing) {
      // De retour en édition, le compte rendu d'un aperçu n'a plus d'objet.
      setCapture(null)
      return undefined
    }
    return holdDryRun()
  }, [previewing])

  useEffect(() => {
    const onCapture = (event) => setCapture(event.detail)
    window.addEventListener(DRY_RUN_EVENT, onCapture)
    return () => window.removeEventListener(DRY_RUN_EVENT, onCapture)
  }, [])

  return (
    <>
      {/* L'annonce tient en une phrase : la charge entière, lue à chaque envoi,
          noierait l'information. La région est TOUJOURS montée — une zone
          `status` créée en même temps que son texte n'est pas annoncée. */}
      <p role="status" aria-live="polite" className="sr-only">
        {capture ? `Envoi à blanc : rien n'a été envoyé vers ${capture.url}.` : ''}
      </p>
      {capture && (
        <section
          aria-labelledby={titleId}
          className="builder-dry-run fixed bottom-4 left-4 right-4 z-40 shadow-lg sm:right-auto sm:w-96"
        >
          <div className="alert alert-warning">
            <div className="flex-1">
              <h2 id={titleId} className="text-sm font-semibold">
                Envoi à blanc : rien n'a été envoyé
              </h2>
              <p className="text-xs">
                En aperçu, les formulaires n'envoient rien. Données qui seraient parties vers{' '}
                <code className="font-mono">{capture.url}</code> :
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCapture(null)}
              className="builder-dry-run-close"
              aria-label="Fermer le compte rendu d'envoi à blanc"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <pre data-testid="builder-dry-run-payload" className="builder-dry-run-payload">
            {readable(capture.data)}
          </pre>
        </section>
      )}
    </>
  )
}
